import React from 'react';
import { Navigate, useParams } from 'react-router';

import { useAuth } from '../auth/useAuth';
import { getNextAfterAuth } from './ProfileSetupRedirect';
import { useResolveJoinDestination } from './useResolveJoinDestination';

interface EntryChoiceGuardProps {
  children: React.ReactNode;
}

/**
 * Spec 026 T021 — guards `/auth-choice` and `/join/:code/auth-choice`:
 * logged-in users skip the entry-choice screen.
 *
 * Later-team-joining: if the user is already a member of a fully-named team
 * in this run, jump them directly into the run shell. Otherwise fall back
 * to `getNextAfterAuth` (dashboard or /join/:code/team).
 */
const EntryChoiceGuard: React.FC<EntryChoiceGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { code } = useParams<{ code?: string }>();
  const destination = useResolveJoinDestination(
    isAuthenticated && code ? code : undefined,
  );

  if (isLoading) return <>{children}</>;
  if (!isAuthenticated) return <>{children}</>;
  if (!code) return <Navigate to={getNextAfterAuth(undefined)} replace />;
  if (destination.kind === 'loading') return null;
  if (destination.kind === 'team-ready') {
    return <Navigate to={`/runs/${destination.runId}/participant/stations`} replace />;
  }
  return <Navigate to={getNextAfterAuth(code)} replace />;
};

export default EntryChoiceGuard;
